import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { formatCLP, statusBadge, formatTracking } from "../../lib/format";
import { CircleNotch, MapPin, Truck, ArrowRight, ClipboardText, CaretLeft, CaretRight } from "@phosphor-icons/react";

const FILTERS = [
  { key: "all", label: "Todos" },
  { key: "active", label: "En curso" },
  { key: "COMPLETADO", label: "Completados" },
  { key: "CANCELADO", label: "Cancelados" },
];

const PAGE_SIZE = 10;

export default function CarrierServices() {
  const [services, setServices] = useState(null);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => { api.get("/services/mine").then((r) => setServices(r.data)).catch(() => setServices([])); }, []);
  useEffect(() => { setPage(1); }, [filter]);

  if (!services) return <div className="flex justify-center py-24"><CircleNotch size={36} className="animate-spin text-[#FF5A36]" /></div>;

  const shown = services.filter((s) => {
    if (filter === "all") return true;
    if (filter === "active") return !["COMPLETADO", "CANCELADO"].includes(s.status);
    return s.status === filter;
  });
  const income = services.filter((s) => s.status === "COMPLETADO").reduce((a, s) => a + (s.agreedPrice || 0), 0);
  const active = services.filter((s) => !["COMPLETADO", "CANCELADO"].includes(s.status)).length;

  const totalPages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * PAGE_SIZE;
  const paged = shown.slice(start, start + PAGE_SIZE);
  const rangeFrom = shown.length === 0 ? 0 : start + 1;
  const rangeTo = Math.min(start + PAGE_SIZE, shown.length);

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-5" data-testid="carrier-services">
      <div className="mb-4">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A36]">Panel del transportista</span>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">Mis servicios</h1>
        <p className="text-sm text-zinc-500 mt-0.5">{services.length} servicios · {active} en curso · {formatCLP(income)} generados</p>
      </div>

      <div className="flex gap-2 mb-3 flex-wrap" data-testid="services-filters">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
            className={`px-4 py-1 rounded-full text-sm font-semibold transition-colors ${filter === f.key ? "bg-[#0F1115] text-white" : "border border-zinc-300 text-zinc-600 hover:border-[#0F1115]"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-300 bg-white p-12 text-center text-zinc-500" data-testid="services-empty">
          <ClipboardText size={40} className="mx-auto text-zinc-300 mb-2" />
          No hay servicios en esta vista.
        </div>
      ) : (
        <div className="rounded-2xl border border-zinc-200 bg-white overflow-x-auto" data-testid="services-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-zinc-400 bg-zinc-50 border-b border-zinc-200">
                <th className="px-4 py-2.5 font-bold">N° Traslado</th>
                <th className="px-4 py-2.5 font-bold">Categoría</th>
                <th className="px-4 py-2.5 font-bold">Ruta</th>
                <th className="px-4 py-2.5 font-bold">Vehículo</th>
                <th className="px-4 py-2.5 font-bold">Precio</th>
                <th className="px-4 py-2.5 font-bold">Estado</th>
                <th className="px-4 py-2.5 font-bold text-right">Acción</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((s) => {
                const badge = statusBadge(s.status);
                return (
                  <tr key={s.id} className="border-b border-zinc-100 hover:bg-zinc-50/60" data-testid={`service-row-${s.id}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-zinc-600 whitespace-nowrap" data-testid={`service-tracking-${s.id}`}>{formatTracking(s.trackingNumber)}</td>
                    <td className="px-4 py-2.5 font-semibold whitespace-nowrap">{s.freight?.category || "—"}</td>
                    <td className="px-4 py-2.5 text-zinc-600">
                      <span className="flex items-center gap-1.5 whitespace-nowrap"><MapPin size={15} className="text-[#FF5A36]" />{s.freight?.origin?.city} → {s.freight?.destination?.city}</span>
                    </td>
                    <td className="px-4 py-2.5 text-zinc-600 whitespace-nowrap">{s.vehicle ? `${s.vehicle.type}${s.vehicle.licensePlate ? ` · ${s.vehicle.licensePlate}` : ""}` : <span className="inline-flex items-center gap-1 text-zinc-400"><Truck size={14} />—</span>}</td>
                    <td className="px-4 py-2.5 font-display font-black whitespace-nowrap">{formatCLP(s.agreedPrice)}</td>
                    <td className="px-4 py-2.5"><span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${badge.color}`}>{badge.label}</span></td>
                    <td className="px-4 py-2.5 text-right">
                      <Link to={`/app/service/${s.id}`} data-testid={`service-view-${s.id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#FF5A36] text-white text-xs font-bold hover:bg-[#E04B2A] active:scale-95">
                        Ver <ArrowRight size={14} weight="bold" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {shown.length > 0 && (
        <div className="flex items-center justify-between mt-3 flex-wrap gap-3" data-testid="services-pagination">
          <span className="text-sm text-zinc-500" data-testid="pagination-range">
            Mostrando {rangeFrom}–{rangeTo} de {shown.length}
          </span>
          <div className="flex items-center gap-2">
            <button data-testid="pagination-prev" disabled={currentPage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-zinc-300 text-sm font-semibold text-zinc-600 hover:border-[#0F1115] disabled:opacity-40 disabled:cursor-not-allowed active:scale-95">
              <CaretLeft size={14} weight="bold" /> Anterior
            </button>
            <span className="text-sm font-semibold text-zinc-700" data-testid="pagination-page">Página {currentPage} de {totalPages}</span>
            <button data-testid="pagination-next" disabled={currentPage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-zinc-300 text-sm font-semibold text-zinc-600 hover:border-[#0F1115] disabled:opacity-40 disabled:cursor-not-allowed active:scale-95">
              Siguiente <CaretRight size={14} weight="bold" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
